import axiosResiInstance from "../../http/axiosResi";
import clone from "../../helper/clone";
import formatPrice from "../../helper/formatPrice";
import getRedirectLocation from "../../helper/getRedirectLocation";
import { getLabelFromRating } from "../form/Stars";
import {
    AGENT_TYPES,
    AGENT_LANGUAGES,
    AGENT_SPECIALITIES,
    GENDERS,
} from "../../service/agent";

AGENT_TYPES.unshift(["", "All"]);
GENDERS.push(["", "Both"]);

const LIST_FIELDS_MAP = {
    type: AGENT_TYPES,
    languages: AGENT_LANGUAGES,
    specialities: AGENT_SPECIALITIES,
    gender: GENDERS,
};

export const DEFAULT_FILTERS = {
    name: {
        type: "string",
        title: "Name",
        value: null,
        label: "",
    },
    location: {
        type: "location",
        title: "Location",
        value: null,
        label: "",
    },
    type: {
        type: "single_list",
        title: "Type",
        value: "",
        label: "",
    },
    specialities: {
        type: "list",
        title: "Speciality",
        value: null,
        label: "",
    },
    languages: {
        type: "list",
        title: "Language",
        value: null,
        label: "",
    },
    gender: {
        type: "single_list",
        title: "Gender",
        value: "",
        label: "",
    },
    rating: {
        type: "rating",
        title: "Min Rating",
        value: 0,
        label: "0 stars",
    },
    price_range: {
        type: "range",
        title: "Price",
        value: null,
        label: "",
    },
};

export const getFiltersFromQuery = async (query) => {
    let filters = clone(DEFAULT_FILTERS);

    for (let [name, value] of Object.entries(query)) {
        if (!(name in filters)) {
            continue;
        }

        if (!value) {
            continue;
        }

        switch (filters[name].type) {
            case "location":
                let loc = null;

                await axiosResiInstance(`/locations/${value}`, {
                    validateStatus: function (status) {
                        const isValid = status >= 200 && status < 300; // default
                        return isValid || 301;
                    },
                }).then((response) => {
                    const redirectLocation = getRedirectLocation(response);

                    if (redirectLocation) {
                        loc = redirectLocation;
                        filters[name].is_redirect = true;
                    } else {
                        loc = response.data[0];
                    }
                });

                filters[name].value = loc;
                filters[name].label = filters[name].value.label;

                break;
            case "list":
                filters[name].value = value.split(",");
                filters[name].label = getLabelFromOptions(
                    LIST_FIELDS_MAP[name],
                    filters[name].value
                );

                break;
            case "single_list":
                filters[name].value = value;
                filters[name].label = getSingleLabelFromOptions(
                    LIST_FIELDS_MAP[name],
                    filters[name].value
                );

                break;
            case "range":
                const range = value.split("-");

                filters[name].value = {
                    min: parseInt(range[0]),
                    max: parseInt(range[1]),
                };
                filters[name].label = getLabelFromPriceRange(
                    filters[name].value
                );

                break;
            case "rating":
                filters[name].value = parseFloat(value);
                filters[name].label = getLabelFromRating(filters[name].value);

                break;
            default:
                filters[name].value = value;
                filters[name].label = value;

                break;
        }
    }

    return filters;
};

export const getQueryFromFilters = (filters) => {
    let query = {};

    Object.entries(filters).map(([name, value]) => {
        if (name === "location") {
            return false;
        }

        let fieldValue = value.value;

        if (!fieldValue) {
            return false;
        }

        switch (value.type) {
            case "list":
                fieldValue = fieldValue.join(",");
                break;
            case "range":
                fieldValue = [fieldValue.min, fieldValue.max].join("-");
                break;
        }

        query[name] = fieldValue;

        return fieldValue;
    });

    return query;
};

export const getApiQueryFromFilters = (filters) => {
    let query = {};

    Object.entries(filters).map(([name, value]) => {
        let fieldValue = value.value;

        if (!fieldValue) {
            return false;
        }

        switch (value.type) {
            case "team_id":
                query["team_id"] = fieldValue;
                break;
            case "list":
                query[name] = fieldValue.join(",");
                break;
            case "range":
                query["price_range__gte"] = fieldValue.min;
                query["price_range__lte"] = fieldValue.max;
                break;
            case "location":
                query[name] = fieldValue.location.id;
                break;
            case "rating":
                query["rating__gte"] = fieldValue;
                break;
            default:
                query[name] = fieldValue;
                break;
        }

        return fieldValue;
    });

    return query;
};

export const getLabelFromOptions = (options, value) => {
    return value
        .map((v) => {
            const option = options.find((s) => s[0] === v);

            return option[1];
        })
        .join(", ");
};

export const getSingleLabelFromOptions = (options, value) => {
    return options.find((s) => s[0] === value)[1];
};

export const getLabelFromPriceRange = (range) => {
    const minLabel = formatPrice(range.min);
    const maxLabel = formatPrice(range.max);

    return `${minLabel} - ${maxLabel}`;
};

export default DEFAULT_FILTERS;
